import { useCallback, useEffect, useRef, useState } from 'react';
import type { FC } from 'react';

import classNames from 'classnames';

import { useNetworkInformation } from '../utils/useNetworkInformation';

import * as styles from './VideoPreview.module.scss';

interface VideoPreviewProps {
  url: string;
  className?: string;
  onEnded?: () => void;
}

export const VideoPreview: FC<VideoPreviewProps> = ({
  url,
  className,
  onEnded,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const ref = useRef<HTMLVideoElement>(null);
  const { shouldSaveData } = useNetworkInformation();

  useEffect(() => {
    const video = ref.current;

    if (!video) {
      return;
    }

    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (entry.isIntersecting) {
          try {
            await video.play();
          } catch {}
        } else {
          video.pause();
        }
      },
      { threshold: 0.8 }
    );

    observer.observe(video);

    return () => observer.disconnect();
  }, []);

  const handleEnded = useCallback(async () => {
    onEnded?.();

    try {
      await ref.current?.play();
    } catch {}
  }, [onEnded]);

  if (shouldSaveData) {
    return null;
  }

  return (
    <div
      className={classNames(className, styles.container, {
        [styles.playing]: isPlaying,
      })}
    >
      <video
        ref={ref}
        className={styles.video}
        controls={false}
        muted
        playsInline
        preload="none"
        disablePictureInPicture
        disableRemotePlayback
        x-webkit-airplay="deny"
        x-webkit-wirelessvideoplaybackdisabled="true"
        src={url}
        onPlay={() => setIsPlaying(() => true)}
        onPause={() => setIsPlaying(() => false)}
        onEnded={handleEnded}
      />
    </div>
  );
};
